// Domain entities - represent the full data model

import type { Address, AccountClassification, Currency, Language } from './common';
import type {
  QBTokens,
  QBConnectionStatus,
  QBCompanyInfo,
  QBCompanySettings,
  QBPreferences,
} from './quickbooks';

// ========================================
// Invoice
// ========================================

export type InvoiceStatus =
  | 'INGESTED'
  | 'PROCESSING'
  | 'REVIEW'
  | 'APPROVAL'
  | 'APPROVED'
  | 'SYNCED'
  | 'REJECTED'
  | 'ON_HOLD'
  | 'FAILED'
  | 'SYNC_FAILED'
  | 'DUPLICATE_FILE'
  // Legacy statuses (for backwards compatibility)
  | 'pending'
  | 'processing'
  | 'pending_approval'
  | 'approved'
  | 'rejected'
  | 'extraction_failed';

export interface InvoiceLineItem {
  id: string;
  description?: string;
  quantity?: number;
  unitPrice?: number;
  amount: number;
  itemCode?: string;
  unitOfMeasure?: string;
  glAccountId?: string;
  glAccountName?: string;
}

export interface VendorInfo {
  name: string;
  address?: Address;
  email?: string;
  phone?: string;
  accountNumber?: string;
}

export interface PartyInfo {
  name?: string;
  address?: Address;
}

export interface ProcessingError {
  code: string;
  message: string;
  timestamp: string;
}

/**
 * Represents a match between extracted invoice data and a QuickBooks entity.
 * Used for vendor matching, and extensible for future entity types (accounts, items, etc.)
 */
export interface EntityMatch {
  /** QuickBooks entity ID (e.g., vendor quickbooksId) */
  entityId: string;
  /** Display name for the matched entity (denormalized) */
  entityName: string;
  /** When the match was made */
  matchedAt: string;
  /** User who performed the match (cognitoSub) */
  matchedBy?: string;
  /** How the match was made */
  matchMethod: 'manual' | 'rule' | 'auto';
  /** If matched by a saved rule, the rule ID */
  ruleId?: string;
}

// ========================================
// Matching Queue & Flags
// ========================================

/**
 * Message format for the invoice matching SQS queue.
 * Sent after invoice processing completes (status = REVIEW).
 */
export interface MatchingQueueMessage {
  type: 'invoice.matching';
  tenantId: string;
  invoiceId: string;
  // Future: matchTypes?: ('vendor' | 'account' | 'term' | 'currency' | 'tax')[];
}

/**
 * Message format for the invoice processing SQS queue.
 * Sent by the intake Lambda after duplicate detection passes.
 */
export interface IntakeQueueMessage {
  type: 'intake.process';
  tenantId: string;
  invoiceId: string;
  s3Bucket: string;
  s3Key: string;
  documentHash: string;
}

/**
 * Flags set when automatic matching encounters conflicts or issues.
 * Used to indicate invoices that need manual review.
 */
export interface MatchingFlags {
  /** Set when multiple vendor matching rules apply to the same invoice */
  vendorConflict?: {
    /** IDs of the conflicting rules */
    matchingRuleIds: string[];
    /** When the conflict was detected */
    flaggedAt: string;
  };
  // Future: accountConflict, termConflict, currencyConflict, taxConflict
}

/**
 * User edits to invoice data.
 * - undefined: field not edited, use extracted value
 * - null: field explicitly cleared by user
 * - string: user-provided value
 */
export interface InvoiceEdits {
  invoiceNumber?: string | null;
  purchaseOrderNumber?: string | null;
  /** Invoice date in YYYY-MM-DD format */
  invoiceDate?: string | null;
  /** Due date in YYYY-MM-DD format */
  dueDate?: string | null;
  /** Notes / comments about the invoice */
  notes?: string | null;
  /** Edited line items (replaces extracted lineItems when present) */
  lineItems?: InvoiceLineItem[] | null;
  /** Edited subtotal amount */
  subtotal?: number | null;
  /** Edited total amount */
  total?: number | null;
  /** Edited billed to party info */
  billedTo?: PartyInfo | null;
  /** Edited shipped to party info */
  shippedTo?: PartyInfo | null;
  /** Edited vendor info */
  vendor?: VendorInfo | null;
}

/**
 * Helper to get the effective value considering edits.
 * @param edited - The edited value (undefined = not edited, null = cleared, string = user value)
 * @param extracted - The original extracted value
 * @returns The effective value to display
 */
export function getEffectiveValue<T>(
  edited: T | null | undefined,
  extracted: T | undefined
): T | undefined {
  if (edited === undefined) return extracted; // not edited
  if (edited === null) return undefined; // explicitly cleared
  return edited; // user's value
}

/**
 * Check if a field has been edited (either to a new value or cleared)
 */
export function isFieldEdited<T>(edited: T | null | undefined): boolean {
  return edited !== undefined;
}

export interface Invoice {
  id: string;
  tenantId: string;
  status: InvoiceStatus;
  s3Key?: string;
  s3Bucket?: string;
  // Extracted data
  vendor?: VendorInfo;
  billedTo?: PartyInfo;
  shippedTo?: PartyInfo;
  invoiceNumber?: string;
  invoiceDate?: string;
  dueDate?: string;
  paymentTerms?: string;
  currency?: Currency;
  lineItems: InvoiceLineItem[];
  subtotal?: number;
  tax?: number;
  total?: number;
  notes?: string;
  confidence?: number;
  // User edits (overrides extracted data)
  edits?: InvoiceEdits;
  // QuickBooks sync
  quickbooksBillId?: string;
  // Entity matching (links extracted data to QuickBooks entities)
  vendorMatch?: EntityMatch;
  paymentTermsMatch?: EntityMatch;
  // Matching flags (set when automatic matching encounters conflicts)
  matchingFlags?: MatchingFlags;
  // Processing info
  errors?: ProcessingError[];
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

// ========================================
// Vendor
// ========================================

export interface Vendor {
  id: string;
  tenantId: string;
  quickbooksId: string;
  displayName: string;
  companyName?: string;
  email?: string;
  phone?: string;
  address?: Address;
  accountNumber?: string;
  active: boolean;
  is1099Contractor?: boolean;
  syncToken?: string;
  syncedAt: string;
}

// ========================================
// Vendor Matching Rule
// ========================================

/**
 * Criteria for matching extracted invoice vendor data to a QuickBooks vendor.
 * All specified fields are AND'd together - all must match for the rule to apply.
 */
export interface VendorMatchCriteria {
  /** Fuzzy match: extracted vendor name must contain this string (case-insensitive) */
  vendorNameContains?: string;
  /** Exact match: extracted vendor name must equal this exactly */
  vendorNameExact?: string;
  /** Exact match on extracted account number */
  accountNumber?: string;
  /** Exact match on extracted email (case-insensitive) */
  email?: string;
  /** Exact match on extracted phone number (normalized) */
  phone?: string;
  /** Exact match on extracted postal/zip code */
  postalCode?: string;
  /** Match invoice currency to vendor's currency (case-insensitive) */
  currency?: string;
}

/**
 * A saved rule that automatically matches invoices to a QuickBooks vendor.
 * When an invoice is processed, rules are evaluated and applied if criteria match.
 */
export interface VendorMatchingRule {
  id: string;
  tenantId: string;
  /** The QuickBooks vendor this rule matches to */
  targetVendorId: string;
  /** Display name of target vendor (denormalized) */
  targetVendorName: string;
  /** Matching criteria - all specified fields must match (AND logic) */
  criteria: VendorMatchCriteria;
  /** Human-readable description of the rule */
  description?: string;
  /** Whether this rule is active */
  active: boolean;
  /** Number of invoices matched by this rule */
  matchCount: number;
  /** When this rule last matched an invoice */
  lastMatchedAt?: string;
  /** When the rule was created */
  createdAt: string;
  /** User who created the rule */
  createdBy: string;
  /** When the rule was last updated */
  updatedAt: string;
}

// ========================================
// GL Account
// ========================================

export interface GLAccount {
  id: string;
  tenantId: string;
  quickbooksId: string;
  name: string;
  accountNumber?: string;
  accountType: string;
  accountSubType?: string;
  classification?: AccountClassification;
  fullyQualifiedName?: string;
  parentId?: string;
  active: boolean;
  syncToken?: string;
  syncedAt: string;
}

// ========================================
// Term (Payment Terms)
// ========================================

/** Type of payment term calculation */
export type TermType = 'STANDARD' | 'DATE_DRIVEN';

/**
 * Payment Terms (synced from QuickBooks)
 * e.g., "Net 30", "Due on Receipt", "2% 10 Net 30"
 */
export interface Term {
  tenantId: string;
  quickbooksId: string;
  name: string;
  active: boolean;
  /** STANDARD (DueDays-based) or DATE_DRIVEN (day of month) */
  type?: TermType;
  /** Number of days until payment is due (for STANDARD type) */
  dueDays?: number;
  /** Discount percentage if paid within discountDays */
  discountPercent?: number;
  /** Number of days to qualify for discount */
  discountDays?: number;
  /** Payment due by this day of month (for DATE_DRIVEN type) */
  dayOfMonthDue?: number;
  /** Discount applies if paid before this day of month */
  discountDayOfMonth?: number;
  /** Payment due next month if issued this many days before dayOfMonthDue */
  dueNextMonthDays?: number;
  // Sync metadata
  syncToken: string;
  syncedAt: string;
  createdAt?: string;
  updatedAt?: string;
}

// ========================================
// CompanyCurrency (Multi-currency support)
// ========================================

/**
 * Company Currency (synced from QuickBooks)
 * Represents a currency enabled for a multi-currency QuickBooks company.
 */
export interface CompanyCurrency {
  tenantId: string;
  quickbooksId: string;
  /** ISO 4217 currency code (e.g., "USD", "EUR", "CAD") */
  code: string;
  /** Full currency name (e.g., "US Dollar", "Euro") */
  name: string;
  /** Whether this currency is active/enabled */
  active: boolean;
  // Sync metadata
  syncToken: string;
  syncedAt: string;
}

// ========================================
// User
// ========================================

export type UserRole = 'viewer' | 'clerk' | 'approver' | 'manager' | 'admin';

export interface User {
  id: string;
  tenantId: string;
  email: string;
  name: string;
  role: UserRole;
  language: Language;
  active: boolean;
  createdAt: string;
}

// ========================================
// Tenant
// ========================================

/**
 * Currency enabled for a tenant (stored in tenant.currencies array).
 * For multi-currency companies, synced from QuickBooks CompanyCurrency.
 * For single-currency companies, just the home currency.
 */
export interface TenantCurrency {
  /** QuickBooks entity ID, or 'HOME' for single-currency companies */
  quickbooksId: string;
  /** ISO 4217 currency code (e.g., "USD", "EUR", "CAD") */
  code: string;
  /** Whether this currency is active/enabled */
  active: boolean;
  /** True for the company's home currency */
  isHomeCurrency: boolean;
  /** Last sync timestamp */
  syncedAt: string;
}

export type SubscriptionTier = 'free' | 'starter' | 'professional' | 'enterprise';

export type SyncStatus = 'idle' | 'in_progress' | 'success' | 'failed';

/**
 * Entity types that can be synced from QuickBooks
 */
export type SyncedEntityType =
  | 'vendors'
  | 'accounts'
  | 'bills'
  | 'terms'
  | 'companyInfo'
  | 'preferences'
  | 'companyCurrencies';

/**
 * Sync info for a single entity type
 */
export interface EntitySyncInfo {
  /** Last full sync timestamp (from syncAll or manual sync) */
  lastFullSyncAt?: string;
  /** Count of records synced in last full sync */
  lastFullSyncCount?: number;
  /** Last webhook-triggered update timestamp */
  lastWebhookUpdateAt?: string;
  /** ID of the last entity updated via webhook */
  lastWebhookEntityId?: string;
  /** Status of the last sync operation */
  lastSyncStatus?: SyncStatus;
  /** Error message if last sync failed */
  lastSyncError?: string;
}

/**
 * Sync tracking for all entity types
 */
export type SyncedEntities = {
  [K in SyncedEntityType]?: EntitySyncInfo;
};

export interface TenantSettings {
  defaultLanguage: Language;
  defaultCurrency: Currency;
  requireApproval?: boolean;
  approvalThreshold?: number;
  /** Decimal places for line item quantity and unit price columns (0-6). Default: 2 */
  lineItemNumericPrecision?: number;
}

/** Default precision for line item numeric values (quantity, unit price) */
export const DEFAULT_LINE_ITEM_NUMERIC_PRECISION = 2;

/**
 * QuickBooks connection data for a tenant.
 *
 * All QuickBooks-related data is nested here to keep the tenant root clean.
 * Raw QB API responses (companyInfo) are stored as-is from Intuit.
 */
export interface TenantQuickBooks {
  // === Raw QB API responses (stored as-is from Intuit) ===
  /** Raw CompanyInfo from QuickBooks /companyinfo endpoint */
  companyInfo?: QBCompanyInfo;
  /** Raw Preferences from QuickBooks /preferences endpoint */
  preferences?: QBPreferences;

  // === OAuth tokens ===
  /** OAuth tokens for QB API access */
  tokens?: QBTokens;

  // === Connection metadata (Acitra-tracked) ===
  /** QuickBooks company ID (realm ID) */
  realmId: string;
  /** When the QB connection was established */
  connectedAt: string;
  /** Who established the connection (user email) */
  connectedBy?: string;
  /** When the connection was disconnected */
  disconnectedAt?: string;
  /** Who/what triggered the disconnect (user email or "system:reason") */
  disconnectedBy?: string;

  // === Derived/transformed data (Acitra-computed from QB data) ===
  /** Transformed company settings from CompanyInfo.NameValue array */
  companySettings?: QBCompanySettings;

  // === Sync state (Acitra-tracked) ===
  /** Last successful sync timestamp (overall) */
  lastSyncAt?: string;
  /** Current sync status (overall) */
  lastSyncStatus?: SyncStatus;
  /** Error message from last failed sync */
  lastSyncError?: string;

  // === Connection health (Acitra-tracked) ===
  /** Current connection status */
  connectionStatus?: QBConnectionStatus;
  /** Last time connection was verified */
  lastConnectionCheckAt?: string;
}

/**
 * Sync tracking data for a tenant.
 *
 * This is Acitra-owned metadata about sync operations,
 * stored separately from raw QuickBooks data.
 */
export interface TenantSync {
  /** Detailed sync info per entity type */
  entities?: SyncedEntities;
}

export interface Tenant {
  // === Acitra-owned fields ===
  id: string;
  name: string;
  subscription: SubscriptionTier;
  settings: TenantSettings;
  createdAt: string;
  createdBy: string;

  // === GSI field (denormalized from quickbooks.realmId for DynamoDB index) ===
  /** QuickBooks realm ID - copied to root level for byRealmId GSI */
  quickbooksRealmId?: string;

  // === QuickBooks connection (all QB data nested here) ===
  /** QuickBooks connection data (all QB-related fields grouped here) */
  quickbooks?: TenantQuickBooks;

  // === Sync tracking (Acitra-owned metadata) ===
  /** Sync tracking data per entity type */
  sync?: TenantSync;

  // === Currency data (synced from QuickBooks) ===
  /** Enabled currencies for this tenant (synced from QB) */
  currencies?: TenantCurrency[];
}

/**
 * Check if a tenant has an active QuickBooks connection.
 */
export function isTenantConnected(tenant: Pick<Tenant, 'quickbooks'>): boolean {
  return !!tenant.quickbooks?.tokens && !tenant.quickbooks?.disconnectedAt;
}

// ========================================
// Tenant-User Relationship
// ========================================

export type TenantUserRole = 'owner' | 'admin' | 'member';

export interface TenantUser {
  tenantId: string;
  cognitoSub: string;
  email: string;
  name?: string;
  role: TenantUserRole;
  invitedBy?: string;
  joinedAt: string;
}

// ========================================
// Tenant Invitation
// ========================================

export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'cancelled';

export interface TenantInvitation {
  id: string;
  tenantId: string;
  email: string;
  role: TenantUserRole;
  invitedBy: string;
  inviterEmail?: string;
  token: string;
  status: InvitationStatus;
  expiresAt: string;
  acceptedAt?: string;
  acceptedBy?: string;
  createdAt: string;
}

// ========================================
// Approval
// ========================================

export type ApprovalStatus = 'pending' | 'approved' | 'rejected';

export interface Approval {
  id: string;
  invoiceId: string;
  tenantId: string;
  approverId: string;
  approverEmail: string;
  step: number;
  status: ApprovalStatus;
  comments?: string;
  decidedAt?: string;
  createdAt: string;
}

// ========================================
// Bill (synced from QuickBooks)
// ========================================

/**
 * Line item detail type for bills
 */
export type BillLineDetailType = 'AccountBasedExpenseLineDetail' | 'ItemBasedExpenseLineDetail';

/**
 * Bill line item (transformed from QB)
 */
export interface BillLineItem {
  id: string;
  lineNum?: number;
  amount: number;
  description?: string;
  detailType: BillLineDetailType;
  // For AccountBasedExpenseLineDetail
  accountId?: string;
  accountName?: string;
  // For ItemBasedExpenseLineDetail
  itemId?: string;
  itemName?: string;
  qty?: number;
  unitPrice?: number;
}

/**
 * Linked transaction reference
 */
export interface BillLinkedTxn {
  txnId: string;
  txnType: string;
}

/**
 * Bill record (synced from QuickBooks)
 * Follows the flattened pattern like Vendor and GLAccount
 */
export interface Bill {
  tenantId: string;
  quickbooksId: string;
  docNumber?: string;
  txnDate?: string;
  dueDate?: string;
  totalAmt: number;
  balance: number;
  currencyCode?: string;
  privateNote?: string;
  // Vendor reference (denormalized)
  vendorId: string;
  vendorName?: string;
  // AP Account reference
  apAccountId?: string;
  apAccountName?: string;
  // Payment Terms reference
  termId?: string;
  termName?: string;
  // Line items
  lines: BillLineItem[];
  // Linked transactions
  linkedTxns?: BillLinkedTxn[];
  // Sync metadata
  syncToken: string;
  syncedAt: string;
  createdAt?: string;
  updatedAt?: string;
}
